/**
 * Project starter templates — pre-seeded data for steps 1-10.
 * These are clearly marked as examples so users know to customise them.
 *
 * Template data maps to the step config in forms/stepConfig.ts.
 * Each entry is the `form` object written to progress/{stepKey} in Firestore.
 */

export interface TemplateStepData {
  [fieldName: string]: string;
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  steps: Record<string, TemplateStepData>; // key = "step1" ... "step10"
}

export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: "saas",
    name: "SaaS Product",
    description: "B2B or B2C software subscription business",
    icon: "bi-cloud-check",
    color: "#3B82F6",
    steps: {
      step1: {
        marketType: "B2B",
        marketSegments:
          "Small and medium-sized businesses (SMBs) with 10-200 employees looking to streamline operations. Example: Operations teams at growing tech startups.",
        customerType: "Business (B2B)",
        primarySegment: "SMB Operations teams",
      },
      step2: {
        beachheadMarket:
          "10-person to 50-person SaaS startups that have outgrown spreadsheets but can't afford enterprise software. Typically Series A-B stage, US/EU based.",
        geography: "United States & Western Europe",
        marketSize: "~500,000 companies in this segment globally",
        whyBeachhead: "High willingness to pay, fast decision cycles, network effects within similar companies",
      },
      step3: {
        primaryPersona:
          "Operations Manager or VP Ops at a 20-50 person SaaS company. Age 28-40, technical enough to evaluate tools, responsible for team productivity and process.",
        personaName: "Sam — Ops Lead",
        demographics: "Age 30, degree-educated, $90k salary, urban, heavy Slack/Notion user",
        psychographics: "Data-driven, process-oriented, frustrated by manual reporting, wants to impress leadership with efficiency gains",
      },
      step4: {
        marketSize: "$12B",
        growthRate: "18% CAGR",
        marketNotes:
          "Global SMB SaaS market growing rapidly post-COVID as remote work drives software adoption. TAM includes all SMB workflow/productivity tools.",
      },
      step5: {
        competitiveAnalysis:
          "Key competitors: Monday.com (complex, expensive), Asana (feature-heavy), Notion (flexible but requires setup). Gap: no tool that auto-generates reports from existing data without configuration.",
        differentiator: "Zero-configuration reporting that works out of the box with existing tools",
        competitorWeaknesses: "High learning curve, requires dedicated ops person to maintain, expensive per-seat pricing",
      },
      step6: {
        fullLifeCycleUse:
          "Problem awareness → Google search → Trial signup → Onboarding (connects integrations) → Weekly reports generated automatically → Shares with CEO → Upgrades to paid → Becomes advocate",
        triggerEvent: "Manual reporting takes 4+ hours/week; missed a key metric in board meeting",
        endGoal: "Board-ready metrics dashboard without manual work",
      },
      step7: {
        decisionMakingUnit:
          "Economic buyer: VP Ops or CEO. Influencer: Engineering lead (for integrations). User: Ops team (2-5 people). Typical sales cycle: 2-4 weeks for SMBs.",
        buyerPersona: "VP Operations — signs off on $500-2k/mo tools independently",
        approvalProcess: "Under $5k/year: ops lead signs alone. Above: CEO approval needed",
      },
      step8: {
        endUserProfile:
          "Ops team members who run reports weekly. 2-5 people. Non-technical but comfortable with SaaS tools. Main pain: copy-pasting data from 6 tools into a spreadsheet every Friday.",
        userNeeds: "Automated data aggregation, customisable templates, Slack/email delivery",
        userFrustrations: "Manual data entry, version control on spreadsheets, inability to drill down",
      },
      step9: {
        shortlistProcess:
          "G2 reviews, LinkedIn ads, founder Slack communities (eg Lenny's Newsletter slack, Y Combinator community). Word of mouth from similar companies.",
        purchaseDrivers: "Free trial, integration list, pricing transparency, quick time-to-value",
        alternatives: "Build in-house (rejected: too slow), hire analyst (rejected: too expensive), use current tools better (rejected: doesn't solve root cause)",
      },
      step10: {
        minimumViableBar:
          "Connects to Salesforce, HubSpot, and Stripe. Auto-generates weekly pipeline and revenue report. Sends to Slack. Works within 30 minutes of signup.",
        dealbreakers: "Must integrate with tools they already use. Must not require IT involvement. Must be GDPR compliant.",
        transferCost: "Low — they're still using spreadsheets, so switching cost is mostly time (2-3 hours to connect integrations)",
      },
    },
  },

  {
    id: "marketplace",
    name: "Marketplace",
    description: "Two-sided marketplace connecting buyers and sellers",
    icon: "bi-shop",
    color: "#8B5CF6",
    steps: {
      step1: {
        marketType: "Two-sided marketplace",
        marketSegments:
          "Supply side: independent professionals / small service providers. Demand side: consumers or businesses seeking specific services. Example: Freelance designers and startups needing design work.",
        customerType: "Both (B2C and B2B depending on demand side)",
        primarySegment: "Supply side is the harder side to acquire — focus there first",
      },
      step2: {
        beachheadMarket:
          "Freelance UX/UI designers in the UK (supply) and early-stage tech startups needing design sprints (demand). Small, concentrated, reachable through Design Twitter and Product Hunt.",
        geography: "United Kingdom — London first, then expand UK-wide",
        marketSize: "~15,000 freelance designers in UK; ~3,000 active early-stage startups",
        whyBeachhead: "Dense network, low incumbent dominance, high repeat purchase potential from startups",
      },
      step3: {
        primaryPersona:
          "Freelance designer (supply side): 3-7 years experience, works solo, struggles with finding quality clients. Demand side: Startup founder at pre-seed/seed stage, needs design help but can't hire full-time.",
        personaName: "Designer: Alex — Freelance UX Lead. Client: Priya — Seed-stage founder",
        demographics: "Alex: Age 29, London, £50k/year freelance income. Priya: Age 32, Cambridge, raised £250k",
        psychographics: "Alex: Values steady income and interesting projects. Priya: Values speed, quality, value for money",
      },
      step4: {
        marketSize: "$15B",
        growthRate: "22% CAGR",
        marketNotes:
          "Global freelance marketplace sector growing fast. Niche marketplaces outperform generalists — vertical focus on design/tech services is underserved vs Upwork/Fiverr.",
      },
      step5: {
        competitiveAnalysis:
          "Upwork/Fiverr: race to bottom on price, poor quality signalling. Toptal: too expensive for startups. Dribbble Jobs: no vetting. Gap: curated, fast-matching design marketplace for funded startups.",
        differentiator: "Vetted designers only (top 10%), 48-hour match SLA, flat-fee project pricing",
        competitorWeaknesses: "Race to bottom pricing, poor trust signals, slow matching, generalist not specialist",
      },
      step6: {
        fullLifeCycleUse:
          "Founder posts brief → Matches with 2-3 vetted designers → 1-hour chemistry call → Accepts proposal → Project runs → Founder reviews → Returns for next project",
        triggerEvent: "Investor feedback says UX is weak; need a designer fast for Series A prep",
        endGoal: "Shipped product with great UX, within budget, fast",
      },
      step7: {
        decisionMakingUnit:
          "Demand side: founder decides alone for projects under £5k. Supply side: designer decides alone whether to join platform.",
        buyerPersona: "Solo founder with product/tech background who undervalues design until a key moment",
        approvalProcess: "No approval needed for most startup clients — founder has full authority",
      },
      step8: {
        endUserProfile:
          "Startup team (demand): 1-5 people, primarily founder and a developer. Designer (supply): solo practitioner, no agency, direct client relationships preferred.",
        userNeeds: "Demand: fast qualified match. Supply: quality clients, fair pay, no chasing invoices",
        userFrustrations: "Demand: wasting time on bad fits. Supply: low-ball offers, scope creep, late payment",
      },
      step9: {
        shortlistProcess:
          "Product Hunt, Design Twitter, Startup Slack communities, AngelList/Wellfound job boards, word of mouth from other founders",
        purchaseDrivers: "Speed of match, portfolio quality, pricing clarity, reviews/testimonials",
        alternatives: "Hire full-time (too expensive), use Fiverr (low quality risk), ask their network (inconsistent availability)",
      },
      step10: {
        minimumViableBar:
          "Matched with at least 2 vetted designers within 48 hours. Designers have been reviewed for quality. Project management tools included. Invoice/payment handled by platform.",
        dealbreakers: "Poor designer quality, slow matching, hidden fees, no dispute resolution",
        transferCost: "Medium — they may already have a Fiverr or Toptal account. Key switch driver: better quality + speed",
      },
    },
  },

  {
    id: "mobile-app",
    name: "Mobile App (Consumer)",
    description: "B2C mobile app with freemium or subscription revenue",
    icon: "bi-phone",
    color: "#EC4899",
    steps: {
      step1: {
        marketType: "B2C",
        marketSegments:
          "Young adults 18-35 who want to improve a specific daily habit (fitness, mindfulness, finance, learning). Mobile-first, app store discovery, social sharing.",
        customerType: "Consumer (B2C)",
        primarySegment: "Habit-focused young professionals, 22-32",
      },
      step2: {
        beachheadMarket:
          "Fitness-focused millennials in urban US cities (NYC, LA, Chicago, Austin) aged 24-30. Already pay for gym membership, willing to pay for premium apps. Reachable via Instagram/TikTok fitness creators.",
        geography: "United States — major cities first",
        marketSize: "~8 million people in US who actively track fitness and pay for apps",
        whyBeachhead: "High intent, high spend, strong word-of-mouth in fitness communities, clear influencer channels",
      },
      step3: {
        primaryPersona:
          "Urban professional, 26, works in tech/marketing, gym goes 3-4x/week. Motivated by aesthetics and performance. Already uses MyFitnessPal but finds it clunky.",
        personaName: "Jordan — Ambitious Millennial",
        demographics: "Age 26, NYC, $70k salary, iPhone user, subscribes to 4+ apps already",
        psychographics: "Progress-obsessed, competitive, shares wins on Instagram, responds to social proof and streaks",
      },
      step4: {
        marketSize: "$8.3B",
        growthRate: "14% CAGR",
        marketNotes:
          "Health and fitness app market growing steadily. Post-COVID home fitness boom. Premium subscriptions replacing one-time purchases. Strong IAP revenue model.",
      },
      step5: {
        competitiveAnalysis:
          "MyFitnessPal: data-heavy, dated UX. Strava: running/cycling only. Whoop: hardware required. Headspace: mindfulness only. Gap: beautiful, all-in-one wellness app with strong gamification.",
        differentiator: "Gamified social wellness tracking — compete with friends, share progress, earn real rewards",
        competitorWeaknesses: "Ugly UX, no social layer, too narrow in scope, high churn after initial motivation fades",
      },
      step6: {
        fullLifeCycleUse:
          "App store discovery (friend share or ad) → Free download → Onboarding (goals, current habits) → First 7-day streak → Converts to premium → Long-term habit formation → Refers friends",
        triggerEvent: "New Year resolution, preparing for a trip, friend joins the app",
        endGoal: "Feel fitter, more in control, part of a community",
      },
      step7: {
        decisionMakingUnit:
          "Individual decides alone. Strong influence from social proof (friend activity, star ratings). Influencer recommendation is key acquisition channel.",
        buyerPersona: "Individual consumer — impulsive to start, then habit-driven to continue",
        approvalProcess: "No approval needed — personal spending decision",
      },
      step8: {
        endUserProfile:
          "Solo user who checks app daily. Also uses app with friends for challenges. Primarily mobile, iOS and Android split 60/40 in this demographic.",
        userNeeds: "Easy logging, progress visualisation, social accountability, personalised coaching",
        userFrustrations: "Complex onboarding, too many notifications, losing streaks, paying for features not used",
      },
      step9: {
        shortlistProcess:
          "App store search and Top Charts. Instagram and TikTok influencer mentions. Friend recommendations. YouTube review videos.",
        purchaseDrivers: "Free trial (7 days), social proof (reviews), friend already on it, clear premium value",
        alternatives: "Instagram fitness influencer programs, paper habit trackers, YouTube fitness, gym personal trainer",
      },
      step10: {
        minimumViableBar:
          "Log a workout in under 60 seconds. See friends' progress. 7-day streak with a satisfying UI. Premium tier includes personalised workout plans.",
        dealbreakers: "Complicated onboarding, privacy concerns around health data, paywall too early, no friends on it",
        transferCost: "Low — apps are cheap and easy to switch. Switching cost builds via streaks and history after 30 days",
      },
    },
  },
];

export const TEMPLATE_MAP: Record<string, ProjectTemplate> = Object.fromEntries(
  PROJECT_TEMPLATES.map((t) => [t.id, t])
);
